import { useState, useEffect } from 'react';
import { useForm } from '@refinedev/antd';
import { Create } from '@refinedev/antd';
import { useCustom } from '@refinedev/core';
import {
  Form,
  Input,
  InputNumber,
  Card,
  Row,
  Col,
  Select,
  Typography,
} from 'antd';
import { LabelWithHelp, SectionTitle } from '../../components/ui/HelpTooltip';

const { Text } = Typography;

interface Region {
  id: number;
  nameUz: string;
}

interface District {
  id: number;
  nameUz: string;
  regionId: number;
}

interface User {
  id: number;
  fullName: string;
  phone: string;
}

export const TripCreate = () => {
  const [pickupRegionId, setPickupRegionId] = useState<number | null>(null);
  const [destinationRegionId, setDestinationRegionId] = useState<number | null>(null);

  const { formProps, saveButtonProps, form } = useForm({
    resource: 'admin/trips',
    redirect: 'list',
  });

  // Fetch regions
  const { data: regionsData } = useCustom<Region[]>({
    url: '/admin/regions',
    method: 'get',
  });

  // Fetch districts for pickup
  const { data: pickupDistrictsData } = useCustom<District[]>({
    url: '/admin/districts',
    method: 'get',
    config: {
      query: pickupRegionId ? { regionId: pickupRegionId } : {},
    },
    queryOptions: {
      enabled: !!pickupRegionId,
    },
  });

  // Fetch districts for destination
  const { data: destinationDistrictsData } = useCustom<District[]>({
    url: '/admin/districts',
    method: 'get',
    config: {
      query: destinationRegionId ? { regionId: destinationRegionId } : {},
    },
    queryOptions: {
      enabled: !!destinationRegionId,
    },
  });

  // Fetch users for client selection
  const { data: usersData } = useCustom<{ data: User[] }>({
    url: '/admin/users',
    method: 'get',
    config: {
      query: { limit: 100 },
    },
  });

  // Fetch drivers for driver selection
  const { data: driversData } = useCustom<{ data: User[] }>({
    url: '/admin/drivers',
    method: 'get',
    config: {
      query: { limit: 100 },
    },
  });

  const regions = regionsData?.data || [];
  const pickupDistricts = pickupDistrictsData?.data || [];
  const destinationDistricts = destinationDistrictsData?.data || [];
  const users = usersData?.data?.data || [];
  const drivers = driversData?.data?.data || [];

  // Reset district when region changes
  useEffect(() => {
    form.setFieldValue('pickupDistrictId', undefined);
  }, [pickupRegionId, form]);

  useEffect(() => {
    form.setFieldValue('destinationDistrictId', undefined);
  }, [destinationRegionId, form]);

  return (
    <Create saveButtonProps={saveButtonProps} title="Yangi sayohat">
      <Form {...formProps} layout="vertical">
        <Row gutter={24}>
          {/* Trip Type and Users */}
          <Col xs={24}>
            <Card
              title={
                <SectionTitle
                  title="Asosiy ma'lumotlar"
                  help="Sayohat turi, mijoz va haydovchi tanlash. Haydovchi bo'sh qoldirilsa, sayohat boshqa haydovchilar uchun ochiq bo'ladi."
                />
              }
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Sayohat turi"
                        help="INTERCITY - bir viloyat ichida, INTERREGION - viloyatlar orasida"
                      />
                    }
                    name="tripType"
                    rules={[{ required: true, message: "Turni tanlang" }]}
                    initialValue="INTERCITY_TAXI"
                  >
                    <Select placeholder="Sayohat turini tanlang">
                      <Select.Option value="INTERCITY_TAXI">
                        Shahar ichida taksi
                      </Select.Option>
                      <Select.Option value="INTERREGION_TAXI">
                        Shaharlararo taksi
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Mijoz"
                        help="Sayohatni buyurtma qilayotgan mijozni tanlang"
                      />
                    }
                    name="clientId"
                    rules={[{ required: true, message: "Mijozni tanlang" }]}
                  >
                    <Select
                      showSearch
                      placeholder="Mijozni qidiring..."
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={users.map((user) => ({
                        value: user.id,
                        label: `${user.fullName || 'Nomsiz'} (${user.phone})`,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Haydovchi"
                        help="Sayohatga haydovchi tayinlash. Bo'sh qoldirilsa, boshqa haydovchilar qabul qilishi mumkin."
                      />
                    }
                    name="driverId"
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Haydovchini qidiring..."
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={drivers.map((driver) => ({
                        value: driver.id,
                        label: `${driver.fullName || 'Nomsiz'} (${driver.phone})`,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Pickup Location */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title="Ketish joyi"
                  help="Mijoz qayerdan olib ketiladi. Viloyat va tumanni tanlab, aniq manzilni kiriting."
                />
              }
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Viloyat"
                    help="Ketish joyi viloyati"
                  />
                }
                name="pickupRegionId"
                rules={[{ required: true, message: "Viloyatni tanlang" }]}
              >
                <Select
                  showSearch
                  placeholder="Viloyatni tanlang"
                  optionFilterProp="children"
                  onChange={(value) => setPickupRegionId(value)}
                  options={regions.map((region) => ({
                    value: region.id,
                    label: region.nameUz,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label="Tuman"
                    help="Ketish joyi tumani. Avval viloyatni tanlang."
                  />
                }
                name="pickupDistrictId"
                rules={[{ required: true, message: "Tumanni tanlang" }]}
              >
                <Select
                  showSearch
                  placeholder={pickupRegionId ? "Tumanni tanlang" : "Avval viloyatni tanlang"}
                  optionFilterProp="children"
                  disabled={!pickupRegionId}
                  options={pickupDistricts.map((district) => ({
                    value: district.id,
                    label: district.nameUz,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label="Manzil"
                    help="Aniq manzil: ko'cha, uy raqami, mo'ljal"
                  />
                }
                name="pickupAddress"
                rules={[{ required: true, message: "Manzilni kiriting" }]}
              >
                <Input placeholder="Amir Temur ko'chasi, 15-uy" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Kenglik (Lat)"
                        help="GPS koordinatasi - kenglik. Google Maps dan olishingiz mumkin."
                      />
                    }
                    name="pickupLat"
                    rules={[{ required: true, message: "Kerak" }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.000001}
                      placeholder="41.311081"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Uzunlik (Long)"
                        help="GPS koordinatasi - uzunlik. Google Maps dan olishingiz mumkin."
                      />
                    }
                    name="pickupLong"
                    rules={[{ required: true, message: "Kerak" }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.000001}
                      placeholder="69.240562"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Destination Location */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title="Borish joyi"
                  help="Mijoz qayerga olib boriladi. Viloyat va tumanni tanlab, aniq manzilni kiriting."
                />
              }
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Viloyat"
                    help="Borish joyi viloyati"
                  />
                }
                name="destinationRegionId"
                rules={[{ required: true, message: "Viloyatni tanlang" }]}
              >
                <Select
                  showSearch
                  placeholder="Viloyatni tanlang"
                  optionFilterProp="children"
                  onChange={(value) => setDestinationRegionId(value)}
                  options={regions.map((region) => ({
                    value: region.id,
                    label: region.nameUz,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label="Tuman"
                    help="Borish joyi tumani. Avval viloyatni tanlang."
                  />
                }
                name="destinationDistrictId"
                rules={[{ required: true, message: "Tumanni tanlang" }]}
              >
                <Select
                  showSearch
                  placeholder={destinationRegionId ? "Tumanni tanlang" : "Avval viloyatni tanlang"}
                  optionFilterProp="children"
                  disabled={!destinationRegionId}
                  options={destinationDistricts.map((district) => ({
                    value: district.id,
                    label: district.nameUz,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label="Manzil"
                    help="Aniq manzil: ko'cha, uy raqami, mo'ljal"
                  />
                }
                name="destinationAddress"
                rules={[{ required: true, message: "Manzilni kiriting" }]}
              >
                <Input placeholder="Registon maydoni" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Kenglik (Lat)"
                        help="GPS koordinatasi - kenglik. Google Maps dan olishingiz mumkin."
                      />
                    }
                    name="destinationLat"
                    rules={[{ required: true, message: "Kerak" }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.000001}
                      placeholder="39.654458"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Uzunlik (Long)"
                        help="GPS koordinatasi - uzunlik. Google Maps dan olishingiz mumkin."
                      />
                    }
                    name="destinationLong"
                    rules={[{ required: true, message: "Kerak" }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.000001}
                      placeholder="66.959688"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Trip Details */}
          <Col xs={24}>
            <Card
              title={
                <SectionTitle
                  title="Sayohat tafsilotlari"
                  help="Sayohat masofasi, vaqti va narxi haqida ma'lumotlar"
                />
              }
            >
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Masofa (km)"
                        help="Ketish va borish joyi orasidagi masofa kilometrda"
                      />
                    }
                    name="distanceKm"
                    rules={[{ required: true, message: "Masofani kiriting" }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      placeholder="300"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Vaqt (soniya)"
                        help="Taxminiy sayohat vaqti soniyada. Bo'sh qoldirilsa, avtomatik hisoblanadi."
                      />
                    }
                    name="durationSeconds"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={60}
                      placeholder="14400"
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Bo'sh qoldirilsa, avtomatik hisoblanadi
                  </Text>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Yo'lovchilar soni"
                        help="Sayohatda nechta yo'lovchi bor"
                      />
                    }
                    name="passengerCount"
                    initialValue={1}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={8}
                      placeholder="1"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Narx (so'm)"
                        help="Sayohat uchun to'lanadigan narx so'mda"
                      />
                    }
                    name="price"
                    rules={[{ required: true, message: "Narxni kiriting" }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1000}
                      step={1000}
                      placeholder="150 000"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                      }
                      parser={(value) =>
                        Number(value?.replace(/\s/g, '') || 0)
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Form>
    </Create>
  );
};
